import { Component, computed, HostListener, inject, Input, OnInit, Output, EventEmitter, TemplateRef } from "@angular/core";
import { CommonModule, NgTemplateOutlet } from "@angular/common";
import { RouterLink, RouterModule } from "@angular/router";
import { FormsModule } from "@angular/forms";
import {
  ContainerComponent,
  DropdownComponent,
  DropdownItemDirective,
  DropdownMenuDirective,
  DropdownToggleDirective,
  HeaderComponent,
  HeaderNavComponent,
  NavItemComponent,
  TooltipDirective,
  ColorModeService,
  BreadcrumbRouterComponent,
  SidebarToggleDirective,
} from "@coreui/angular";
import { IconDirective } from "@coreui/icons-angular";
import { BsModalRef, BsModalService } from "ngx-bootstrap/modal";
import { AuthService } from "@app/services/auth/auth.service";
import { Router } from "@angular/router";
import { ProfileResolver } from "@app/resolvers/profile.resolver";
import { UserService } from "@app/services/user/user.service";
import { NotificationService } from "@app/services/notification/notification.service";
import { ThemeService } from "@app/services/theme/theme.service";

@Component({
  selector: "app-default-header",
  host: {
    '[class.header-collapsed]': 'hiddenHeader'
  },
  templateUrl: "./default-header.component.html",
  standalone: true,
  imports: [
    BreadcrumbRouterComponent,
    CommonModule,
    ContainerComponent,
    DropdownComponent,
    DropdownItemDirective,
    DropdownMenuDirective,
    DropdownToggleDirective,
    FormsModule,
    HeaderComponent,
    HeaderNavComponent,
    IconDirective,
    NavItemComponent,
    NgTemplateOutlet,
    RouterLink,
    RouterModule,
    SidebarToggleDirective,
    TooltipDirective,
  ],
  styles: [`
    :host {
      display: block;
      position: relative;
    }

    :host.header-collapsed {
      height: 0 !important;
      min-height: 0 !important;
      max-height: 0 !important;
      margin: 0 !important;
      padding: 0 !important;
      overflow: visible;
      background: transparent !important;
      border: 0 !important;
      box-shadow: none !important;
    }

    /* Fits the screen on phones instead of running off its left edge. */
    .notifications-menu {
      min-width: min(470px, calc(100vw - 1.5rem));
      max-height: 450px;
      overflow-y: auto;
    }

    /* Pulled into the header's side padding so it sits near the screen edge. */
    .menu-toggle {
      margin-left: -0.75rem;
    }

    .user-avatar {
      width: 38px;
      height: 38px;
      border-radius: 50%;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      font-weight: bold;
      color: white;
      font-size: 0.8rem;
      line-height: 1;
    }

    .header-toggle-floating {
      position: absolute;
      left: 50%;
      transform: translateX(-50%);
      z-index: 1040;
      width: 34px;
      height: 34px;
      border: 0;
      border-radius: 0;
      padding: 0;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      /* Theme text colour: a fixed near-black disappeared on the dark header. */
      color: var(--cui-body-color);
      background: transparent;
      font-size: 1.6rem;
      line-height: 1;
      cursor: pointer;
      top: calc(100% - 28px);
      box-shadow: none;
    }

    .header-toggle-floating:hover {
      color: var(--cui-emphasis-color);
      background: transparent;
    }

    .header-toggle-hidden {
      top: -0.75rem;
      width: 46px;
      height: 46px;
      color: #ffffff;
      font-size: 1.9rem;
      text-shadow: 0 1px 3px rgba(0, 0, 0, 0.95), 0 0 1px rgba(0, 0, 0, 0.95);
    }
  `]
})
export class DefaultHeaderComponent implements OnInit {
  private readonly avatarColorPalette: Record<string, string> = {
    red: "#ef4444",
    green: "#34d399",
    blue: "#60a5fa",
    gray: "#9ca3af",
    indigo: "#818cf8",
    purple: "#a78bfa",
    primary: "#60a5fa",
  };

  @Input() notifications: any[] = [];
  @Input() notCount: number = 0;
  @Input() profile: any;
  
  @Output() asideToggle = new EventEmitter<void>();
  
  readonly #colorModeService = inject(ColorModeService);
  readonly colorMode = this.#colorModeService.colorMode;

  // Unified theme options - controls both CoreUI and custom themes
  readonly unifiedThemes = [
    { name: 'light', text: 'Light', icon: 'cilSun' },
    { name: 'dark', text: 'Dark', icon: 'cilMoon' }
  ];

  readonly icons = computed(() => {
    const currentTheme = this.getCurrentUnifiedTheme();
    return this.unifiedThemes.find(theme => theme.name === currentTheme)?.icon ?? 'cilMoon';
  });

  public sidebarMinimized = sessionStorage.getItem("hiddenHeader") === "true";
  hiddenHeader = sessionStorage.getItem("hiddenHeader") === "true";
  modalRef!: BsModalRef;

  constructor(
    private authService: AuthService,
    private route: Router,
    private profileService: ProfileResolver,
    private userService: UserService,
    private notificationService: NotificationService,
    private modalService: BsModalService,
    private themeService: ThemeService
  ) {}

  ngOnInit() {
    if (window.innerWidth < 1200) this.sidebarMinimized = true;
    this.syncHeaderHiddenClass();
    
    // Initialize unified theme system
    this.initializeUnifiedTheme();
  }

  private initializeUnifiedTheme() {
    // Get saved theme from localStorage or default to dark
    const savedTheme = localStorage.getItem('unifiedTheme') || 'dark';
    this.setUnifiedTheme(savedTheme);
  }

  setUnifiedTheme(themeName: string) {
    // Set both CoreUI color mode and custom theme
    this.colorMode.set(themeName);
    this.themeService.setThemeByName(themeName);
    localStorage.setItem('unifiedTheme', themeName);
  }

  getCurrentUnifiedTheme(): string {
    return localStorage.getItem('unifiedTheme') || this.themeService.getCurrentTheme();
  }

  toggleMinimize(e: boolean) {
    this.sidebarMinimized = typeof e === 'boolean' ? e : !this.sidebarMinimized;
    sessionStorage.setItem(
      "hiddenHeader",
      this.hiddenHeader ? "true" : "false"
    );
    this.syncHeaderHiddenClass();
  }

  toggleHeaderVisibility() {
    this.hiddenHeader = !this.hiddenHeader;
    this.toggleMinimize(this.hiddenHeader);
  }

  private syncHeaderHiddenClass() {
    if (this.hiddenHeader) document.body.classList.add("header-hidden");
    else document.body.classList.remove("header-hidden");
  }

  logout() {
    this.authService.logout().subscribe(() => {
      this.route.navigate(["/login"]);
    });
  }

  redirect(item: any) {
    if (!item.readed) {
      this.notificationService.readNotification(item.id).subscribe();
      this.notCount--;
    }
    item.readed = true;

    this.route.navigate([
      "/work-orders",
      item.name == "qb-pay-failed" ? item.work_order_id : item.model,
    ]);
  }

  removeNotification(item: any) {
    this.notificationService.deleteNotification(item.id).subscribe(() => {
      if (!item.readed) this.notCount--;
      this.notifications.splice(
        this.notifications.findIndex((x) => x.id == item.id),
        1
      );
    });
  }

  readAll() {
    this.notificationService
      .readAll({ user_id: this.profileService.profileInfo.id })
      .subscribe(() => {
        this.notifications.forEach((x) => (x.readed = true));
        this.notCount = 0;
      });
  }

  clearAll() {
    this.notificationService
      .clearAll({ user_id: this.profileService.profileInfo.id })
      .subscribe(() => {
        this.notifications = [];
        this.notCount = 0;
      });
  }

  redirectToWP(form: any) {
    if (this.profileService.profileInfo.wp_id || !form)
      window.open("https://fieldtechfitness.com/support/", "_blank");
    else {
      this.userService
        .wpSync(this.profileService.profileInfo.id, {
          password: form.value.password,
        })
        .subscribe((response) => {
          this.profileService.profileInfo.wp_id = response.data;
          window.open("https://fieldtechfitness.com/support/", "_blank");
          this.modalRef.hide();
        });
    }
  }

  checkWP(template: TemplateRef<any>) {
    this.userService
      .checkWP(this.profileService.profileInfo.id)
      .subscribe((response) => {
        if (response.founded) this.redirectToWP(null);
        else this.openModal(template);
      });
  }

  openModal(template: TemplateRef<any>) {
    this.modalRef = this.modalService.show(template);
  }

  /** The Super Admin layout has no sidebar, so there is nothing to open. */
  get showMenuToggle() {
    return !this.profileService.isSuperAdmin();
  }

  @HostListener('window:resize', ['$event'])
  getScreenSize(event: any) {
    if (event.target.innerWidth < 1200) this.sidebarMinimized = true;
    else this.sidebarMinimized = false;
  }

  /**
   * Mirrors what AuthGuardService checks on /time-clock, so the link is only offered to people
   * the route will let in. Checking the company add-on alone showed it to a Super Admin, which
   * holds no permissions, and the click ended on the 401 page.
   */
  get canGoToTimeClock() {
    // admin_company is null for a Super Admin, which belongs to no company. Reading through it
    // unguarded threw while the user menu rendered, so the Logout entry below never appeared.
    return (
      !this.profileService.isClient() &&
      !this.profileService.isAdminClient() &&
      !!this.profileService.profileInfo?.admin_company?.time_clock &&
      this.profileService.hasPermission("Time clock")
    );
  }

  /** A Super Admin manages its own account inside the admin panel, not on the company profile. */
  get profileRoute(): string[] {
    return this.profileService.isSuperAdmin() ? ["/admin", "account"] : ["/profile"];
  }

  toggleAside() {
    this.asideToggle.emit();
  }

  get avatarBackgroundColor() {
    return this.avatarColorPalette[this.profile?.components_color] || this.avatarColorPalette.primary;
  }
}
